import { Component, computed, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatTableModule } from '@angular/material/table';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { ConfigurationWaterfall } from '../configuration-waterfall.service';
import { WorkspaceStore } from '../workspace-store';
import { DiffView } from '../diff-view/diff-view';
import { EnvironmentMeta } from '../environment-meta/environment-meta';
import {
  DiffLine, Environment, PayloadKind, TableItem, TextFn,
  comparedLines as comparedLinesFor, contentLabel as contentLabelFor, environmentLabel, environments,
  filterRows, groupRows, hasData as hasDataFor, identity, pairStatus as pairStatusFor, pairSymbol as pairSymbolFor,
} from '../configuration-view';

@Component({
  selector: 'app-compare',
  imports: [
    FormsModule, MatButtonModule, MatCardModule, MatFormFieldModule, MatIconModule, MatInputModule, MatTableModule,
    TranslocoPipe, DiffView, EnvironmentMeta,
  ],
  templateUrl: './compare-page.html',
  styleUrl: './compare-page.scss',
})
export class ComparePage {
  private readonly transloco = inject(TranslocoService);
  private readonly workspace = inject(WorkspaceStore);
  readonly rows = this.workspace.rows;
  readonly loading = this.workspace.loading;
  readonly loaded = this.workspace.loaded;
  readonly busyLabel = this.workspace.busyLabel;
  readonly search = this.workspace.search;
  readonly renderMode = this.workspace.renderMode;
  readonly leftEnvironment = this.workspace.leftEnvironment;
  readonly rightEnvironment = this.workspace.rightEnvironment;
  readonly selected = this.workspace.selected;

  readonly environments = environments;
  readonly compareColumns = ['configuration', 'leftEnv', 'pair', 'rightEnv'];
  readonly filtered = computed(() => filterRows(this.rows(), this.search()));
  readonly grouped = computed(() => groupRows(this.filtered()));
  readonly isGroup = (_index: number, item: TableItem): boolean => item.kind === 'group';
  readonly isRow = (_index: number, item: TableItem): boolean => item.kind === 'row';
  private readonly text: TextFn = key => this.transloco.translate(key);

  label(environment: Environment): string {
    return environmentLabel(environment);
  }

  hasData(row: ConfigurationWaterfall, environment: Environment): boolean {
    return hasDataFor(row, environment);
  }

  contentLabel(row: ConfigurationWaterfall, environment: Environment): string {
    return contentLabelFor(row, environment, this.text);
  }

  pairStatus(row: ConfigurationWaterfall): 'missing' | 'equal' | 'different' {
    return pairStatusFor(row, this.leftEnvironment(), this.rightEnvironment(), this.renderMode(), this.transloco.getActiveLang(), this.text);
  }

  pairSymbol(row: ConfigurationWaterfall): string {
    return pairSymbolFor(row, this.leftEnvironment(), this.rightEnvironment(), this.renderMode(), this.transloco.getActiveLang(), this.text);
  }

  lines(row: ConfigurationWaterfall, kind: PayloadKind, side: 'left' | 'right'): DiffLine[] {
    return comparedLinesFor(row, this.leftEnvironment(), this.rightEnvironment(), kind, side, this.renderMode(), this.text);
  }

  isSelected(item: TableItem): boolean {
    if (item.kind !== 'row') return false;
    const selected = this.selected();
    return selected !== null && identity(selected) === identity(item.row);
  }

  selectRow(item: TableItem): void {
    if (item.kind !== 'row') return;
    const selected = this.selected();
    if (!selected || identity(selected) !== identity(item.row)) this.workspace.dismissOverlays();
    this.selected.set(item.row);
  }

  closePreview(): void {
    this.selected.set(null);
    this.workspace.dismissOverlays();
  }

  refresh(): void {
    this.workspace.refresh();
  }

  edit(): void {
    this.workspace.openEditor();
  }

  showHistory(request: { row: ConfigurationWaterfall; environment: Environment }): void {
    this.workspace.openHistory(request);
  }

  swapEnvironments(): void {
    const left = this.leftEnvironment();
    this.leftEnvironment.set(this.rightEnvironment());
    this.rightEnvironment.set(left);
  }
}
